import React, {useState} from "react";
import {Controller} from "react-hook-form";
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  TextInput,
  TouchableOpacity,
  Platform,
} from "react-native";
import {globalFontStyle} from "../assets";
import Colors from "../assets/Color/Colors";
import FastImage from "../common/FastImage";
import {globalImport} from "../common/globalImports";
import {globalConfigurableData, GlobalScreens} from "../utils/globalConstant";
import {convert} from "../locale/translateText";

const CustomInput = ({
  control,
  name,
  rules,
  placeholder = "",
  isEditable = true,
  isNumberKeyboard = false,
  isFullScreenField = false,
  isTextAlignLeft = false,
  isIncrementBtnShow = false,
  isTextInput = false,
  isLoginStyle = false,
  parentStyle = {},
  onBtnClick,
  customStyle = {},
  onInputFocus,
  selectTextOnFocus,
  translate = false,
  isPointerEvent = true,
  scale = null, // can be "0-100", "0-10", "0-1" or null for text input
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const isNumericInput = scale !== null;

  const handleChangeText = (text, onChange) => {
    if (!scale || isTextInput) {
      onChange(text);
      return;
    }

    // Handle numeric input based on scale
    const numericValue = text.replace(/[^0-9.]/g, '');
    
    if (numericValue === '') {
      onChange('');
      return;
    }

    const value = parseFloat(numericValue);
    
    switch(scale) {
      case "0-100":
        if (value >= 0 && value <= 100) {
          onChange(numericValue);
        }
        break;
      case "0-10":
        if (value >= 0 && value <= 10) {
          onChange(numericValue);
        }
        break;
      case "0-1":
        if (value >= 0 && value <= 1) {
          // Allow only one decimal place
          const parts = numericValue.split('.');
          if (parts[1] && parts[1].length > 1) {
            onChange(value.toFixed(1));
          } else {
            onChange(numericValue);
          }
        }
        break;
      default:
        onChange(numericValue);
    }
  };

  return (
    <View
      style={[
        styles.inputContainer,
        isLoginStyle ? {flex: 1} : {},
        parentStyle,
      ]}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({field: {value, onChange, onBlur}, fieldState: {error}}) => (
          <>
            <View style={{position: "relative"}}>
              {isIncrementBtnShow ? (
                <View style={[styles.plusbtnLeft]}>
                  <TouchableOpacity
                    style={styles.buttonContainer}
                    onPress={() => {
                      onBtnClick("decrement");
                    }}>
                    <FastImage
                      style={{width: 35, height: 35}}
                      source={globalConfigurableData.ic_plus()}
                    />
                  </TouchableOpacity>
                </View>
              ) : null}

              <TextInput
                value={value}
                onChangeText={(text) => handleChangeText(text, onChange)}
                pointerEvents={isPointerEvent ? "auto" : "none"}
                onBlur={(e) => {
                  setIsFocused(false);
                  onBlur(e);
                }}
                onFocus={(e) => {
                  setIsFocused(true);
                  onInputFocus && onInputFocus(e);
                }}
                selectTextOnFocus={selectTextOnFocus}
                selectionColor="rgba(0, 0, 0, 0.5)"
                editable={isEditable}
                placeholder={translate ? convert(placeholder) : placeholder}
                placeholderTextColor={
                  globalConfigurableData.secondaryTextColor1
                }
                style={[
                  isLoginStyle
                    ? styles.inputLogin
                    : isFullScreenField
                    ? styles.inputFull
                    : isNumericInput
                    ? styles.inputNumeric
                    : styles.input,
                  isTextAlignLeft ? {textAlign: "left"} : null,
                  customStyle,
                  isIncrementBtnShow ? {height: 55, paddingRight: 50} : null,
                  isFocused ? styles.inputFocused : null,
                ]}
                keyboardType={
                  isTextInput
                    ? "default"
                    : isNumberKeyboard || scale
                    ? Platform.OS === "ios" ? "decimal-pad" : "numeric"
                    : "decimal-pad"
                }
                outlineColor="transparent"
                activeOutlineColor="transparent"
                underlineColorAndroid="transparent"
              />
              {isIncrementBtnShow ? (
                <View style={styles.plusbtn}>
                  <TouchableOpacity
                    style={styles.buttonContainer}
                    onPress={() => {
                      onBtnClick("increment");
                    }}>
                    <FastImage
                      style={{width: 35, height: 35}}
                      source={globalConfigurableData.ic_minus()}
                    />
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          </>
        )}
      />
    </View>
  );
};

export default CustomInput;

const styles = StyleSheet.create({
  inputContainer: {
    minWidth: 100,
    borderRadius: 6,
    backgroundColor: GlobalScreens.SingleOrder.inputColor,
    flex: 1,
  },
  input: {
    borderRadius: 6,
    borderWidth: 1,
    borderColor: globalConfigurableData.borderColor,
    paddingVertical: 12,
    paddingHorizontal: 12,
    textAlign: "right",
    color: globalConfigurableData.primaryTextColor1,
    backgroundColor: Colors.bgWhite,
    ...globalFontStyle.appFontSubHeading,
  },
  inputNumeric: {
    borderRadius: 6,
    borderWidth: 1,
    borderColor: globalConfigurableData.borderColor,
    height: 48,
    paddingVertical: 14,
    paddingHorizontal: 12,
    textAlign: "right",
    color: globalConfigurableData.primaryTextColor1,
    backgroundColor: Colors.bgWhite,
    ...globalFontStyle.appFontSubHeading,
  },
  inputFocused: {
    borderColor: globalConfigurableData.primaryTextColor1,
    borderWidth: 1.5,
    outlineWidth: 0,
    outlineColor: "transparent",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  inputLogin: {
    borderRadius: 6,
    height: 46,
    borderWidth: 1,
    borderColor: globalConfigurableData.borderColor,
    paddingVertical: 0,
    paddingHorizontal: 16,
    color: globalConfigurableData.primaryTextColor1,
    backgroundColor: Colors.bgWhite,
    ...globalFontStyle.appFontBody,
    width: '100%',
    textAlign: "left",
    lineHeight: 46,
    fontSize: 15,
  },
  inputFull: {
    borderRadius: 6,
    borderWidth: 1,
    borderColor: globalConfigurableData.borderColor,
    height: 46,
    paddingHorizontal: 16,
    paddingVertical: 0,
    color: globalConfigurableData.primaryTextColor1,
    backgroundColor: Colors.bgWhite,
    ...globalFontStyle.appFontBody,
    width: '100%',
  },
  plusbtn: {
    padding: 0,
    margin: 0,
    position: "absolute",
    right: 10,
    zIndex: 100,
    flexDirection: "row",
    top: 10,
  },
  plusbtnLeft: {
    left: 10,
    padding: 0,
    margin: 0,
    position: "absolute",
    zIndex: 100,
    flexDirection: "row",
    top: 10,
  },
  buttonContainer: {
    borderRadius: 4,
    overflow: 'hidden'
  },
  btnBlk: {
    backgroundColor: "#F8F8F8",
    borderColor: globalConfigurableData.borderColor,
    borderWidth: Platform.OS === "ios" ? 0.5 : 0.22,
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: "center",
    justifyContent: "center",
  },
  btnBlkText: {
    textAlign: "center",
  },
});
